import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  OpsStaffList,
  OpsStaffMetaData,
  OpsStaffData,
} from '../services/opsStaffService';
import {
  getOpsStaffList,
  getOpsStaffMetaData,
  saveOrUpdateOpsStaff,
  getOpsStaffDetailsById,
} from '../services/opsStaffService';

export interface OpsStaffListQueryPerams {
  userId: string;
  page?: number;
  size?: number;
  sortField?: string;
  sortType?: string;
  searchTerm?: string;
  statusId?: number[];
  roleId?: number[];
}

export const useGetOpsStaffList = ({
  page = 0,
  size = 10,
  sortField,
  sortType,
  searchTerm,
  statusId,
  roleId,
}: OpsStaffListQueryPerams) => {
  const { data, isLoading, isError, error, refetch } = useQuery<OpsStaffList>({
    queryKey: [
      'OpsStaffListResponse',
      page,
      size,
      sortField,
      sortType,
      searchTerm,
      statusId,
      roleId,
    ],
    queryFn: () =>
      getOpsStaffList({
        page,
        size,
        sortField,
        sortType,
        searchTerm,
        statusId,
        roleId,
      }),
    staleTime: 0,
    gcTime: 0,
  });

  return {
    OpsStaffListResponse: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetOpsStaffMetaData = (userId: string) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<OpsStaffMetaData>({
      queryKey: ['OpsStaffMetaData', userId],
      queryFn: async () => {
        const data = await getOpsStaffMetaData(userId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
    });

  return {
    OpsStaffMetaData: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useSaveOrUpdateOpsStaff = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<
    OpsStaffData,
    Error,
    OpsStaffData & { userId: string; action: string }
  >({
    mutationFn: async ({ userId, action, ...payload }) => {
      const data = await saveOrUpdateOpsStaff(userId, action, payload);
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['OpsStaffListResponse'],
      });
      onSuccess();
    },
    onError: (error) => {
      console.error('Error saving or updating ops user:', error);
    },
  });
};

export const useGetOpsStaffDetailsById = (
  userId: string,
  opsStaffId: number
) => {
  const { data, isLoading, isError, error, refetch } = useQuery<OpsStaffData>({
    queryKey: ['OpsStaffDetailsById', userId, opsStaffId],
    queryFn: async () => {
      const data = await getOpsStaffDetailsById(userId, opsStaffId);
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    enabled: !!opsStaffId,
  });

  return {
    OpsStaffDetailsById: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};
